import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  CircularProgress, 
  Alert, 
  Chip,
  Grid,
  Paper,
  Card,
  CardMedia,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Videocam as VideocamIcon,
  Fullscreen as FullscreenIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  ViewModule as ViewModuleIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import axios from 'axios';
import Hls from 'hls.js';
import { API_ORIGIN } from '../config';
import { pickCctvPlayUrl, pickCctvLivePlayback } from '../utils/cctvStreamUrl';
import { preferHlsWorker, tryPlayMutedInline } from '../utils/hlsPlayback';
import { usePlaybackConfig } from '../hooks/usePlaybackConfig';
import { fetchStreamJpegBlob, isRtspUrl } from '../utils/streamPreviewSnapshot';
import Go2rtcWhepPlayer from './cctv/Go2rtcWhepPlayer';

// CCTV 타입 정의
interface Cctv {
  id: number;
  pool_id: number;
  name: string;
  location: string;
  url?: string;
  stream_url?: string;
  playback_url?: string;
  webrtc_url?: string;
  status: 'active' | 'inactive' | 'maintenance';
  camera_type: 'safety' | 'management' | 'other';
  created_at: string;
  updated_at: string;
  purpose?: string;
}

// 컴포넌트 Props 정의
interface RealCCTVTabProps {
  poolId: number;
  poolCode?: string;
  /** 시설 단독 관제: 중앙 playback 대신 stream_url */
  useRemotePlaybackOverride?: boolean;
  siteGatewayEnabled?: boolean;
  preferLocalRtspSnapshot?: boolean;
}

type CameraType = 'all' | 'safety' | 'management' | 'control' | 'other';

/** 부모 리렌더마다 새 컴포넌트 타입이 되면 video/WebRTC가 리마운트되어 깜빡임 — 모듈 스코프 유지 */
const EmptyCCTVSlot: React.FC<{ row: number; col: number }> = ({ row, col }) => (
  <Box
    sx={{
      height: '100%',
      bgcolor: 'black',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 0,
    }}
  >
    <Typography color="text.disabled" sx={{ fontWeight: 400 }}>
      위치 {row}-{col}
    </Typography>
  </Box>
);

type RealCctvCardProps = {
  cctv: Cctv;
  onUpdate?: () => void;
  useRemotePlayback: boolean;
  siteGatewayEnabled: boolean;
  preferLocalRtspSnapshot: boolean;
  centralGo2rtcAvailable: boolean;
  centralGo2rtcStreams: string[];
};

const RealCctvCard: React.FC<RealCctvCardProps> = React.memo(
  ({
    cctv,
    onUpdate,
    useRemotePlayback,
    siteGatewayEnabled,
    preferLocalRtspSnapshot,
    centralGo2rtcAvailable,
    centralGo2rtcStreams,
  }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const snapshotUrlRef = useRef<string | null>(null);
    const webrtcFallbackRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [streamUrl, setStreamUrl] = useState(() =>
      pickCctvPlayUrl(
        {
          id: cctv.id,
          pool_id: cctv.pool_id,
          stream_url: cctv.stream_url,
          url: cctv.url,
          playback_url: cctv.playback_url,
        },
        useRemotePlayback,
        siteGatewayEnabled
      )
    );
    const [snapshotSrc, setSnapshotSrc] = useState<string | null>(null);
    const rawStream = (cctv.stream_url || cctv.url || '').trim();
    const useRtspSnapshot =
      preferLocalRtspSnapshot && Boolean(rawStream) && isRtspUrl(rawStream);
    const streamKey =
      cctv.pool_id && cctv.id ? `p${cctv.pool_id}_c${cctv.id}` : '';
    const live = pickCctvLivePlayback(
      {
        id: cctv.id,
        pool_id: cctv.pool_id,
        stream_url: cctv.stream_url,
        url: cctv.url,
        playback_url: cctv.playback_url,
        webrtc_url: cctv.webrtc_url,
      },
      useRemotePlayback,
      siteGatewayEnabled,
      centralGo2rtcAvailable,
      centralGo2rtcStreams
    );
    const [playbackMode, setPlaybackMode] = useState<'webrtc' | 'hls'>(() =>
      live.mode === 'webrtc' ? 'webrtc' : 'hls'
    );

    const resolvePlayUrl = useCallback(
      (row: Cctv) =>
        pickCctvPlayUrl(
          {
            id: row.id,
            pool_id: row.pool_id,
            stream_url: row.stream_url,
            url: row.url,
            playback_url: row.playback_url,
          },
          useRemotePlayback,
          siteGatewayEnabled
        ),
      [useRemotePlayback, siteGatewayEnabled]
    );

    useEffect(() => {
      webrtcFallbackRef.current = false;
      setPlaybackMode(live.mode === 'webrtc' ? 'webrtc' : 'hls');
      // eslint-disable-next-line react-hooks/exhaustive-deps -- CCTV 전환 시에만 모드 리셋
    }, [streamKey]);

    useEffect(() => {
      if (live.mode === 'hls' && playbackMode === 'webrtc') {
        setPlaybackMode('hls');
      } else if (
        live.mode === 'webrtc' &&
        playbackMode === 'hls' &&
        !webrtcFallbackRef.current
      ) {
        setPlaybackMode('webrtc');
      }
    }, [live.mode, playbackMode]);

    const hlsStreamSrc = useMemo(
      () => live.hlsUrl || resolvePlayUrl(cctv),
      [live.hlsUrl, cctv.id, cctv.pool_id, cctv.stream_url, cctv.url, cctv.playback_url, resolvePlayUrl]
    );

    const handleWebrtcFallback = useCallback((_reason: string) => {
      webrtcFallbackRef.current = true;
      setPlaybackMode('hls');
    }, []);

    const handleWebrtcPlaying = useCallback(() => {
      setError(null);
    }, []);

    useEffect(() => {
      if (!useRtspSnapshot) return;
      let cancelled = false;
      const clearSnap = () => {
        if (snapshotUrlRef.current) {
          URL.revokeObjectURL(snapshotUrlRef.current);
          snapshotUrlRef.current = null;
        }
      };
      const load = async () => {
        try {
          const blob = await fetchStreamJpegBlob(rawStream);
          if (cancelled) return;
          clearSnap();
          const obj = URL.createObjectURL(blob);
          snapshotUrlRef.current = obj;
          setSnapshotSrc(obj);
          setError(null);
        } catch (err) {
          console.error(`[CCTVCard] RTSP 스냅샷 실패 (CCTV ${cctv.id}):`, err);
          if (!cancelled) setError('현장 RTSP 스냅샷 실패 (DataGateway 확인)');
        }
      };
      void load();
      const timer = window.setInterval(() => {
        void load();
      }, 2500);
      return () => {
        cancelled = true;
        window.clearInterval(timer);
        clearSnap();
      };
    }, [useRtspSnapshot, rawStream, cctv.id]);

    useEffect(() => {
      if (useRtspSnapshot || playbackMode !== 'hls') return;
      const video = videoRef.current;
      if (!video || !hlsStreamSrc) {
        if (!hlsStreamSrc) {
          setError(
            '스트림 URL이 없음. 게이트웨이 HLS(playback_url) 또는 stream_url을 확인.'
          );
        }
        return;
      }

      let cancelled = false;

      if (hlsStreamSrc.includes('.m3u8') && Hls.isSupported()) {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }

        const hls = new Hls({
          enableWorker: preferHlsWorker(),
          lowLatencyMode: false,
          liveSyncDurationCount: 3,
          backBufferLength: 30,
          maxBufferLength: 15,
          maxMaxBufferLength: 30,
          manifestLoadingMaxRetry: 4,
          levelLoadingMaxRetry: 4,
          fragLoadingMaxRetry: 4,
        });
        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_event, data) => {
          console.error(`[CCTVCard] HLS 에러 (CCTV ${cctv.id}):`, data);
          if (data.fatal) {
            setError('스트림 로드 실패');
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (cancelled) return;
          void tryPlayMutedInline(video)
            .then(() => {
              setError(null);
            })
            .catch((err) => {
              console.error(`[CCTVCard] 비디오 재생 실패 (CCTV ${cctv.id}):`, err);
              setError('비디오 재생 실패');
            });
        });

        hls.loadSource(hlsStreamSrc);
        hls.attachMedia(video);
      } else {
        video.src = hlsStreamSrc;
        void tryPlayMutedInline(video)
          .then(() => {
            setError(null);
          })
          .catch((err) => {
            console.error(`[CCTVCard] 비디오 재생 실패 (CCTV ${cctv.id}):`, err);
            setError('비디오 재생 실패');
          });
      }

      return () => {
        cancelled = true;
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        video.removeAttribute('src');
        video.load();
      };
    }, [useRtspSnapshot, playbackMode, hlsStreamSrc, cctv.id]);

    const handleUpdateStreamUrl = async () => {
      if (!streamUrl || streamUrl === cctv.stream_url || streamUrl === cctv.url) {
        setIsSettingsOpen(false);
        return;
      }

      try {
        const response = await axios.put(`${API_ORIGIN}/api/cctvs/${cctv.id}`, {
          stream_url: streamUrl,
          name: cctv.name,
          location: cctv.location,
          status: cctv.status,
          camera_type: cctv.camera_type,
          purpose: cctv.purpose,
        });

        if (response.status === 200) {
          onUpdate?.();
        }
        setIsSettingsOpen(false);
      } catch (err) {
        console.error(`[RealCCTVTab] CCTV ${cctv.id} 스트림 URL 업데이트 실패:`, err);
        setError('스트림 URL 업데이트에 실패');
      }
    };

    const handleOpenInNewWindow = (row: Cctv) => {
      const baseWidth = 1280;
      const baseHeight = 720;
      const maxWidth = window.screen.width * 0.9;
      const maxHeight = window.screen.height * 0.9;
      const windowWidth = Math.min(baseWidth, maxWidth);
      const windowHeight = Math.min(baseHeight, maxHeight);
      const left = (window.screen.width - windowWidth) / 2;
      const top = (window.screen.height - windowHeight) / 2;

      const newWindow = window.open(
        '',
        `CCTV_${row.id}`,
        `width=${windowWidth},height=${windowHeight},left=${left},top=${top}`
      );

      if (!newWindow) return;
      const playUrl = resolvePlayUrl(row);
      newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${row.name} - CCTV 모니터링</title>
              <script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
              <style>
                body { margin: 0; padding: 0; background: #000; display: flex; flex-direction: column; height: 100vh; overflow: hidden; }
                .header { background: #1a1a1a; color: white; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; font-size: 14px; height: 20px; }
                .video-container { flex: 1; position: relative; background: #000; display: flex; align-items: center; justify-content: center; }
                video { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; background: #000; }
                .loading { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; background: rgba(0,0,0,0.7); padding: 10px 20px; border-radius: 4px; }
                .error { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #ff4444; background: rgba(0,0,0,0.7); padding: 10px 20px; border-radius: 4px; }
              </style>
            </head>
            <body>
              <div class="header"><div>${row.name}</div><div>위치: ${row.location}</div></div>
              <div class="video-container">
                <video id="video" autoplay muted playsinline></video>
                <div id="loading" class="loading">로딩 중...</div>
                <div id="error" class="error" style="display: none;"></div>
              </div>
              <script>
                const video = document.getElementById('video');
                const loading = document.getElementById('loading');
                const error = document.getElementById('error');
                const streamUrl = ${JSON.stringify(playUrl)};
                function hideLoading() { loading.style.display = 'none'; }
                function showError(message) { loading.style.display = 'none'; error.textContent = message; error.style.display = 'block'; }
                if (streamUrl.includes('.m3u8') && Hls.isSupported()) {
                  const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
                  hls.on(Hls.Events.ERROR, (event, data) => { if (data.fatal) showError('스트림 로드 실패'); });
                  hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().then(hideLoading).catch(() => showError('재생 실패')); });
                  hls.loadSource(streamUrl);
                  hls.attachMedia(video);
                } else {
                  video.src = streamUrl;
                  video.addEventListener('loadeddata', () => { video.play().then(hideLoading).catch(() => showError('재생 실패')); });
                  video.addEventListener('error', () => showError('스트림 로드 실패'));
                }
              </script>
            </body>
          </html>
        `);
      newWindow.document.close();
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{cctv.name}</span>
              {!useRtspSnapshot && (
                <Chip
                  size="small"
                  label={playbackMode === 'webrtc' ? 'WebRTC' : 'HLS'}
                  color={playbackMode === 'webrtc' ? 'success' : 'default'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          }
          subheader={`위치: ${cctv.location}`}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => handleOpenInNewWindow(cctv)}
                size="small"
                title="새 창에서 크게 보기"
              >
                <VideocamIcon />
              </IconButton>
              <IconButton
                onClick={() => setIsSettingsOpen(true)}
                size="small"
                title="스트림 URL 수정"
              >
                <SettingsIcon />
              </IconButton>
            </Box>
          }
        />
        <Box sx={{ position: 'relative', flex: 1 }}>
          {useRtspSnapshot ? (
            snapshotSrc ? (
              <img
                src={snapshotSrc}
                alt={cctv.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#000',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#000',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )
          ) : playbackMode === 'webrtc' && live.webrtcUrl ? (
            <Go2rtcWhepPlayer
              whepUrl={live.webrtcUrl}
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#000',
              }}
              onFallback={handleWebrtcFallback}
              onPlaying={handleWebrtcPlaying}
            />
          ) : (
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#000',
              }}
              muted
              playsInline
              autoPlay
            />
          )}
          {error && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
        </Box>
        <Dialog
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>스트림 URL 수정</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <TextField
                size="small"
                value={streamUrl}
                onChange={(e) => setStreamUrl(e.target.value)}
                sx={{ width: '300px' }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleUpdateStreamUrl}
                disabled={!streamUrl}
              >
                스트림 URL 수정
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsSettingsOpen(false)} color="inherit">
              취소
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }
);

const RealCCTVTab: React.FC<RealCCTVTabProps> = React.memo(({
  poolId,
  poolCode,
  useRemotePlaybackOverride,
  siteGatewayEnabled = true,
  preferLocalRtspSnapshot = false,
}) => {
  const {
    useRemotePlayback: remoteFromConfig,
    centralGo2rtcAvailable,
    centralGo2rtcStreams,
  } = usePlaybackConfig();
  const useRemotePlayback =
    useRemotePlaybackOverride !== undefined
      ? useRemotePlaybackOverride
      : remoteFromConfig;

  const resolvePlayUrl = useCallback(
    (cctv: Cctv) =>
      pickCctvPlayUrl(
        {
          id: cctv.id,
          pool_id: cctv.pool_id,
          stream_url: cctv.stream_url,
          url: cctv.url,
          playback_url: cctv.playback_url,
        },
        useRemotePlayback,
        siteGatewayEnabled
      ),
    [useRemotePlayback, siteGatewayEnabled]
  );

  // 상태 관리
  const [cctvs, setCctvs] = useState<Cctv[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraType, setCameraType] = useState<CameraType>('control');
  const [gridSize, setGridSize] = useState<string>('auto');
  const [fitMode, setFitMode] = useState<'contain' | 'cover'>('contain');
  const [openWindows, setOpenWindows] = useState<{ [key: number]: Window }>({});
  const initialLoadRef = useRef<boolean>(false);
  const prevPoolIdRef = useRef<number | null>(null);

  // API URL 설정
  const API_URL = API_ORIGIN;

  // 카메라 유형에 따른 표시 텍스트 (헬퍼 함수)
  const getCameraTypeText = (type: CameraType): string => {
    switch (type) {
      case 'safety': return '안전 모니터링';
      case 'management': return '관리 모니터링';
      case 'control': return '관제 모니터링';
      case 'other': return '기타 모니터링';
      case 'all': return '모든 카메라';
      default: return '알 수 없음';
    }
  };
  
  // 카메라 유형에 따른 색상
  const getCameraTypeColor = (type: string) => {
    switch (type) {
      case 'safety': return 'error';
      case 'management': return 'primary';
      case 'control': return 'success';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  // 카메라 타입 맵핑 함수 (purpose -> camera_type)
  const mapPurposeToType = (purpose: string | undefined): CameraType => {
    if (!purpose) return 'other';
    
    // 서버에서 사용하는 용도 값에 따라 적절한 카메라 타입으로 매핑
    if (purpose.includes('안전') || purpose.toLowerCase().includes('safety')) {
      return 'safety';
    } else if (purpose.includes('관리') || purpose.toLowerCase().includes('management')) {
      return 'management';
    } else if (purpose.includes('관제') || purpose.toLowerCase().includes('control')) {
      return 'control';
    } else {
      return 'other';
    }
  };

  // 상태 값 표시 함수 추가
  const getStatusText = (status: string | undefined): string => {
    if (!status) return '알 수 없음';
    
    // 대소문자 구분 없이 비교를 위해 소문자로 변환
    const lowerStatus = status.toLowerCase();
    
    if (lowerStatus.includes('active')) return '활성';
    if (lowerStatus.includes('inactive')) return '비활성';
    if (lowerStatus.includes('maintenance')) return '유지보수 중';
    if (lowerStatus.includes('error')) return '오류';
    
    // 기본값 반환
    return status;
  };

  // 새 창과의 통신 처리
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'newWindowReady') {
        const { cctvId } = event.data;
        setOpenWindows(prev => ({
          ...prev,
          [cctvId]: event.source as Window
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // CCTV 데이터 가져오기
  const fetchCCTVs = useCallback(async () => {
    if (loading || (prevPoolIdRef.current === poolId && cctvs.length > 0)) return;
    
    try {
      setLoading(true);
      setError(null);
      prevPoolIdRef.current = poolId;
      
      console.log(`[RealCCTVTab] CCTV 정보 조회 시작 (poolId: ${poolId}, poolCode: ${poolCode})`);
      
      const response = await axios.get(`/api/pools/${poolId}/cctvs`);
      
      if (response.data && response.status >= 200 && response.status < 300) {
        console.log('[RealCCTVTab] CCTV 정보 조회 성공:', response.data);
        
        const cctvData = response.data.data || response.data;
        
        if (Array.isArray(cctvData) && cctvData.length > 0) {
          const processedCctvs = cctvData.map((cctv: any, index: number) => {
            const cameraType = mapPurposeToType(cctv.purpose);
            
            if (!cctv.location || !cctv.location.match(/\d+-\d+/)) {
              const row = Math.floor(index / 4) + 1;
              const col = (index % 4) + 1;
              return {
                ...cctv,
                location: `${row}-${col}`,
                camera_type: cameraType,
                url: cctv.url || cctv.stream_url,
                stream_url: cctv.stream_url || cctv.url,
                playback_url: cctv.playback_url,
              };
            }
            return {
              ...cctv,
              camera_type: cameraType,
              url: cctv.url || cctv.stream_url,
              stream_url: cctv.stream_url || cctv.url,
              playback_url: cctv.playback_url,
            };
          });
          
          setCctvs(processedCctvs);
        } else {
          setCctvs([]);
          console.log('[RealCCTVTab] CCTV 데이터가 없거나 비어있습니다.');
        }
      } else {
        throw new Error(response.data?.message || 'CCTV 데이터를 가져오기 실패');
      }
    } catch (error) {
      console.error('[RealCCTVTab] CCTV 정보 조회 실패:', error);
      setError('CCTV 정보를 불러오기 실패');
    } finally {
      setLoading(false);
    }
  }, [poolId, poolCode, loading, cctvs.length]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchCCTVs();
    }
  }, [fetchCCTVs]);

  // poolId가 변경될 때만 데이터 다시 로드
  useEffect(() => {
    if (initialLoadRef.current && prevPoolIdRef.current !== poolId) {
      fetchCCTVs();
    }
  }, [poolId, fetchCCTVs]);

  // 카메라 타입 필터링
  const getFilteredCCTVs = () => {
    if (cameraType === 'all') {
      return cctvs;
    }
    
    // camera_type으로 필터링하고, 추가로 purpose도 확인
    return cctvs.filter(cctv => {
      // 이미 매핑된 camera_type으로 체크
      if (cctv.camera_type === cameraType) return true;
      
      // purpose 값으로 확인 (camera_type이 없거나 맞지 않을 경우)
      if (cameraType === 'safety' && cctv.purpose && 
          (cctv.purpose.includes('안전') || cctv.purpose.toLowerCase().includes('safety'))) {
        return true;
      }
      
      if (cameraType === 'management' && cctv.purpose && 
          (cctv.purpose.includes('관리') || cctv.purpose.toLowerCase().includes('management'))) {
        return true;
      }
      
      return false;
    });
  };

  // 카메라 타입 핸들러
  const handleCameraTypeChange = (event: SelectChangeEvent<string>) => {
    setCameraType(event.target.value as CameraType);
  };

  // 그리드 크기 핸들러
  const handleGridSizeChange = (event: SelectChangeEvent<string>) => {
    setGridSize(event.target.value);
  };

  // 카메라 위치를 기반으로 그리드 구성을 위한 함수
  const organizeByGridPosition = () => {
    const filteredCCTVs = getFilteredCCTVs();
    
    // 행과 열 최대값 찾기
    let maxRow = 0;
    let maxCol = 0;
    
    filteredCCTVs.forEach(cctv => {
      const [row, col] = cctv.location.split('-').map(Number);
      if (isNaN(row) || isNaN(col)) return; // 잘못된 위치 정보는 무시
      maxRow = Math.max(maxRow, row);
      maxCol = Math.max(maxCol, col);
    });
    
    // 최소 그리드 크기 보장 (1x1 이상)
    maxRow = Math.max(maxRow, 1);
    maxCol = Math.max(maxCol, 1);
    
    // 그리드 생성
    const grid: (Cctv | null)[][] = [];
    
    // 그리드 초기화
    for (let i = 0; i <= maxRow; i++) {
      grid[i] = [];
      for (let j = 0; j <= maxCol; j++) {
        grid[i][j] = null;
      }
    }
    
    // 카메라를 그리드에 배치
    filteredCCTVs.forEach(cctv => {
      const [row, col] = cctv.location.split('-').map(Number);
      if (!isNaN(row) && !isNaN(col) && row > 0 && col > 0 && row <= maxRow && col <= maxCol) {
        grid[row][col] = cctv;
      } else {
        // 위치 정보가 없거나 잘못된 경우 빈 셀 찾아서 배치
        for (let i = 1; i <= maxRow; i++) {
          for (let j = 1; j <= maxCol; j++) {
            if (!grid[i][j]) {
              grid[i][j] = cctv;
              // 배치 후 루프 종료
              i = maxRow + 1;
              j = maxCol + 1;
            }
          }
        }
      }
    });
    
    return { grid, maxRow, maxCol };
  };

  // 새 창에서 전체 그리드 열기 함수
  const openGridInNewWindow = () => {
    const filteredCctvs = getFilteredCCTVs();
    if (filteredCctvs.length === 0) return;
    
    // 그리드 레이아웃 설정
    let maxRow, maxCol, grid;
    
    if (gridSize === 'auto') {
      // 위치 기반 그리드 정보 가져오기
      const result = organizeByGridPosition();
      grid = result.grid;
      maxRow = result.maxRow;
      maxCol = result.maxCol;
    } else {
      // 선택된 그리드 크기에 따라 분할
      const [rows, cols] = gridSize.split('x').map(Number);
      maxRow = rows;
      maxCol = cols;
      
      // 그리드 생성
      grid = [];
      for (let i = 0; i <= maxRow; i++) {
        grid[i] = [];
        for (let j = 0; j <= maxCol; j++) {
          grid[i][j] = null;
        }
      }
      
      // CCTV를 그리드에 순차적으로 배치
      let cctvIndex = 0;
      for (let i = 1; i <= maxRow && cctvIndex < filteredCctvs.length; i++) {
        for (let j = 1; j <= maxCol && cctvIndex < filteredCctvs.length; j++) {
          grid[i][j] = filteredCctvs[cctvIndex++];
        }
      }
    }
    
    // 새 창에 표시할 HTML 생성
    let gridHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>CCTV 그리드 모니터링</title>
        <script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
        <style>
          body, html { 
            margin: 0; 
            padding: 0; 
            height: 100%; 
            overflow: hidden; 
            background-color: #000; 
            font-family: Arial; 
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .grid-container { 
            display: grid; 
            width: 100%; 
            height: 100vh; 
            grid-gap: 0 !important; 
            gap: 0 !important; 
            padding: 0 !important; 
            margin: 0 !important;
            box-sizing: border-box; 
            grid-template-rows: repeat(${maxRow}, 1fr);
            grid-template-columns: repeat(${maxCol}, 1fr);
            border-collapse: collapse;
            background-color: #000;
            border: none !important;
            aspect-ratio: ${maxCol * 16} / ${maxRow * 9};
            max-width: 100vw;
            max-height: 100vh;
          }
          .cctv-cell { 
            position: relative; 
            overflow: hidden; 
            background-color: #000; 
            border: none !important; 
            padding: 0 !important;
            margin: 0 !important;
            box-sizing: border-box;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            aspect-ratio: 16 / 9;
          }
          .cctv-video { 
            width: 100%; 
            height: 100%; 
            object-fit: contain; 
            display: block;
            background-color: #000;
            margin: 0 !important;
            padding: 0 !important;
          }
          .cctv-name { 
            position: absolute; 
            top: 10px; 
            right: 10px; 
            background: rgba(0,0,0,0.5); 
            color: white; 
            padding: 3px 8px; 
            font-size: 12px;
            font-weight: 400;
            z-index: 10;
            margin: 0;
            border-radius: 2px;
          }
          .empty-cell { 
            background-color: #000;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            width: 100%;
            height: 100%;
            aspect-ratio: 16 / 9;
          }
          .loading-indicator {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            text-align: center;
            z-index: 9;
            font-size: 12px;
            background: rgba(0,0,0,0.7);
            padding: 5px 10px;
            border-radius: 3px;
          }
          .debug-info {
            position: fixed;
            bottom: 10px;
            right: 10px;
            color: rgba(255,255,255,0.5);
            font-size: 12px;
            z-index: 100;
          }
          .video-container {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #000;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
            aspect-ratio: 16 / 9;
          }
          * {
            box-sizing: border-box !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
          }
          .fullscreen-btn {
            position: fixed;
            bottom: 15px;
            right: 15px;
            background: rgba(0,0,0,0.7);
            color: white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 1000;
          }
          .fullscreen-btn svg {
            width: 24px;
            height: 24px;
          }
        </style>
      </head>
      <body>
        <div class="grid-container">
    `;
    
    // 그리드 셀 추가
    for (let i = 1; i <= maxRow; i++) {
      for (let j = 1; j <= maxCol; j++) {
        const cctv = grid[i] && grid[i][j];
        
        if (cctv) {
          const streamUrl = resolvePlayUrl(cctv);
          const videoId = `video-${cctv.id}`;
          
          gridHtml += `
            <div class="cctv-cell" style="grid-row: ${i}; grid-column: ${j};">
              <div class="video-container">
                <video id="${videoId}" class="cctv-video" autoplay muted playsinline></video>
              </div>
              <div class="loading-indicator" id="loading-${videoId}">로딩 중...</div>
              <div class="cctv-name">${cctv.name}</div>
            </div>
          `;
        } else {
          gridHtml += `
            <div class="empty-cell" style="grid-row: ${i}; grid-column: ${j};"></div>
          `;
        }
      }
    }
    
    // 디버그 정보 및 전체화면 버튼 추가
    gridHtml += `
      </div>
      <div class="debug-info">
        그리드 크기: ${maxRow} x ${maxCol}, CCTV 수: ${filteredCctvs.length}
      </div>
      <div class="fullscreen-btn" id="fullscreen-toggle" title="전체화면 전환">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
        </svg>
      </div>
      <script>
        console.log("CCTV 모니터링 초기화 시작");
        
        // 비디오 초기화 함수
        function initializeVideos() {
          console.log("비디오 초기화 함수 실행");
    `;
    
    // 각 CCTV에 대한 비디오 초기화 스크립트 추가
    let cctvCount = 0;
    for (let i = 1; i <= maxRow; i++) {
      for (let j = 1; j <= maxCol; j++) {
        const cctv = grid[i] && grid[i][j];
        if (!cctv) continue;
        
        cctvCount++;
        const streamUrl = resolvePlayUrl(cctv);
        if (!streamUrl) continue;
        
        const videoId = `video-${cctv.id}`;
        
        gridHtml += `
          (function() {
            const videoElement = document.getElementById('${videoId}');
            const loadingElement = document.getElementById('loading-${videoId}');
            if (!videoElement) {
              console.error("비디오 요소를 찾을 수 없음: ${videoId}");
              return;
            }
            
            const streamUrl = '${streamUrl}';
            console.log("CCTV ${cctv.name} 초기화 - URL: " + streamUrl);
            
            function hideLoading() {
              if (loadingElement) loadingElement.style.display = 'none';
            }
            
            function showError(message) {
              if (loadingElement) {
                loadingElement.textContent = "오류: " + message;
                loadingElement.style.color = 'red';
              }
            }
            
            if (streamUrl.includes('.m3u8') && Hls.isSupported()) {
              try {
                const hls = new Hls({
                  enableWorker: true,
                  lowLatencyMode: true
                });
                
                hls.on(Hls.Events.ERROR, (event, data) => {
                  console.error("HLS 에러:", event, data);
                  if (data.fatal) {
                    showError("스트림 로드 실패");
                  }
                });
                
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                  videoElement.play()
                    .then(() => {
                      hideLoading();
                      console.log("${cctv.name} 재생 시작");
                    })
                    .catch(err => {
                      console.error("자동 재생 실패:", err);
                      showError("재생 실패");
                    });
                });
                
                hls.loadSource(streamUrl);
                hls.attachMedia(videoElement);
              } catch (e) {
                console.error("HLS 초기화 오류:", e);
                showError("초기화 실패");
              }
            } else {
              try {
                videoElement.src = streamUrl;
                
                videoElement.addEventListener('loadeddata', () => {
                  videoElement.play()
                    .then(() => {
                      hideLoading();
                      console.log("${cctv.name} 재생 시작");
                    })
                    .catch(err => {
                      console.error("자동 재생 실패:", err);
                      showError("재생 실패");
                    });
                });
                
                videoElement.addEventListener('error', (e) => {
                  console.error("비디오 로드 오류:", e);
                  showError("스트림 로드 실패");
                });
              } catch (e) {
                console.error("비디오 초기화 오류:", e);
                showError("초기화 실패");
              }
            }
          })();
        `;
      }
    }
    
    gridHtml += `
          console.log("총 ${cctvCount}개 CCTV 초기화 완료");
        }
        
        // 페이지 로드 시 비디오 초기화
        document.addEventListener('DOMContentLoaded', () => {
          initializeVideos();
          
          // 전체화면 버튼 이벤트 리스너 추가
          const fullscreenBtn = document.getElementById('fullscreen-toggle');
          if (fullscreenBtn) {
            fullscreenBtn.addEventListener('click', () => {
              if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch(err => {
                  console.error('전체화면 전환 실패:', err);
                });
              } else {
                document.exitFullscreen().catch(err => {
                  console.error('전체화면 종료 실패:', err);
                });
              }
            });
          }
          
          // 전체화면 변경 이벤트 리스너
          document.addEventListener('fullscreenchange', () => {
            const gridContainer = document.querySelector('.grid-container');
            if (document.fullscreenElement) {
              // 전체화면 모드일 때
              const screenWidth = window.screen.width;
              const screenHeight = window.screen.height;
              const aspectRatio = ${maxCol * 16} / ${maxRow * 9};
              
              // 화면 폭을 기준으로 높이 계산
              const newHeight = screenWidth / aspectRatio;
              
              // 그리드 컨테이너 크기 조정
              gridContainer.style.width = '100vw';
              gridContainer.style.height = newHeight + 'px';
              gridContainer.style.maxHeight = '100vh';
            } else {
              // 전체화면 모드 해제 시
              gridContainer.style.width = '100%';
              gridContainer.style.height = '100vh';
            }
          });
        });
      </script>
    </body>
    </html>
    `;
    
    // 새 창 열기 - 화면 크기에 맞게 조정
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    
    // CCTV 화면 비율 계산 (16:9 가정)
    const aspectRatio = 16 / 9;
    const totalCCTVs = filteredCctvs.length;
    
    // 그리드 레이아웃에 따른 행과 열 계산
    let rows, cols;
    if (gridSize === 'auto') {
      const result = organizeByGridPosition();
      rows = result.maxRow;
      cols = result.maxCol;
    } else {
      [rows, cols] = gridSize.split('x').map(Number);
    }
    
    // 전체 화면 비율 계산
    const totalAspectRatio = (cols * aspectRatio) / rows;
    
    // 화면 크기 계산 (화면의 90%를 사용)
    let windowWidth, windowHeight;
    if (totalAspectRatio > 1) {
      // 가로가 더 긴 경우
      windowWidth = Math.round(screenWidth * 0.9);
      windowHeight = Math.round(windowWidth / totalAspectRatio);
    } else {
      // 세로가 더 긴 경우
      windowHeight = Math.round(screenHeight * 0.9);
      windowWidth = Math.round(windowHeight * totalAspectRatio);
    }
    
    // 화면 크기가 너무 크지 않도록 제한
    windowWidth = Math.min(windowWidth, screenWidth * 0.9);
    windowHeight = Math.min(windowHeight, screenHeight * 0.9);
    
    const newWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight},left=${(screenWidth-windowWidth)/2},top=${(screenHeight-windowHeight)/2}`);
    if (newWindow) {
      newWindow.document.write(gridHtml);
      newWindow.document.close();
      console.log(`[RealCCTVTab] ${filteredCctvs.length}개 CCTV로 새 창 열림, 그리드 크기: ${rows}x${cols}, 창 크기: ${windowWidth}x${windowHeight}`);
    }
  };

  // 여백 없이 보기 모드 토글
  const toggleFitMode = () => {
    setFitMode(prev => {
      const newMode = prev === 'contain' ? 'cover' : 'contain';
      console.log(`[RealCCTVTab] 화면 맞춤 모드 변경: ${prev} → ${newMode} (${newMode === 'contain' ? '원본 비율 유지' : '여백 없이 보기'})`);
      return newMode;
    });
  };

  // CCTV 그리드 렌더링
  const renderCCTVGrid = () => {
    const filteredCCTVs = getFilteredCCTVs();
    
    if (filteredCCTVs.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
            표시할 CCTV가 없음
          </Typography>
        </Box>
      );
    }
    
    // 위치 기반 자동 그리드 또는 선택된 그리드 크기에 따라 렌더링
    if (gridSize === 'auto') {
      // 위치 기반 그리드 구성 정보
      const { grid, maxRow, maxCol } = organizeByGridPosition();
      
      return (
        <Box>
          <Grid container spacing={0}>
            {Array.from({ length: maxRow }).map((_, rowIndex) => {
              const row = rowIndex + 1;
              
              return (
                <Grid item xs={12} key={`row-${row}`}>
                  <Grid container spacing={0}>
                    {Array.from({ length: maxCol }).map((_, colIndex) => {
                      const col = colIndex + 1;
                      const cctv = grid[row] && grid[row][col];
                      
                      return (
                        <Grid item xs={12 / maxCol} key={`cell-${row}-${col}`} sx={{ padding: 0 }}>
                          <Box sx={{ p: 0, height: '100%', minHeight: 250 }}>
                            {cctv ? (
                              <RealCctvCard
                              cctv={cctv}
                              onUpdate={fetchCCTVs}
                              useRemotePlayback={useRemotePlayback}
                              siteGatewayEnabled={siteGatewayEnabled}
                              preferLocalRtspSnapshot={preferLocalRtspSnapshot}
                              centralGo2rtcAvailable={centralGo2rtcAvailable}
                              centralGo2rtcStreams={centralGo2rtcStreams}
                            />
                            ) : (
                              <EmptyCCTVSlot row={row} col={col} />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    } else {
      // 선택된 그리드 크기에 따라 분할
      const [rows, cols] = gridSize.split('x').map(Number);
      
      // 각 셀에 CCTV 할당
      const cells = [];
      for (let i = 0; i < rows * cols; i++) {
        cells.push(filteredCCTVs[i] || null);
      }
      
      return (
        <Box>
          <Grid container spacing={0}>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <Grid item xs={12} key={`row-${rowIndex}`}>
                <Grid container spacing={0}>
                  {Array.from({ length: cols }).map((_, colIndex) => {
                    const index = rowIndex * cols + colIndex;
                    const cctv = cells[index];
                    
                    return (
                      <Grid item xs={12 / cols} key={`cell-${rowIndex}-${colIndex}`} sx={{ padding: 0 }}>
                        <Box sx={{ p: 0, height: '100%', minHeight: 250 }}>
                          {cctv ? (
                            <RealCctvCard
                              cctv={cctv}
                              onUpdate={fetchCCTVs}
                              useRemotePlayback={useRemotePlayback}
                              siteGatewayEnabled={siteGatewayEnabled}
                              preferLocalRtspSnapshot={preferLocalRtspSnapshot}
                              centralGo2rtcAvailable={centralGo2rtcAvailable}
                              centralGo2rtcStreams={centralGo2rtcStreams}
                            />
                          ) : (
                            <EmptyCCTVSlot row={rowIndex + 1} col={colIndex + 1} />
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }
  };
  
  // 로딩 컴포넌트
  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <CircularProgress />
      <Typography variant="body1" sx={{ ml: 2 }}>
        CCTV 정보를 불러오는 중...
      </Typography>
    </Box>
  );

  // 오류 컴포넌트
  const renderError = () => (
    <Alert 
      severity="error" 
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={fetchCCTVs}>
          재시도
        </Button>
      }
    >
      {error}
    </Alert>
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* 헤더 및 제어 영역 */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 2, fontWeight: 400 }}>
            실시간 CCTV 모니터링
          </Typography>
          <Chip 
            label="실제 데이터" 
            color="primary" 
            size="small" 
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>카메라 유형</InputLabel>
            <Select
              value={cameraType}
              label="카메라 유형"
              onChange={handleCameraTypeChange}
            >
              <MenuItem value="all" sx={{ fontWeight: 400 }}>모든 카메라</MenuItem>
              <MenuItem value="control" sx={{ fontWeight: 400 }}>관제 모니터링</MenuItem>
              <MenuItem value="safety" sx={{ fontWeight: 400 }}>안전 모니터링</MenuItem>
              <MenuItem value="management" sx={{ fontWeight: 400 }}>관리 모니터링</MenuItem>
              <MenuItem value="other" sx={{ fontWeight: 400 }}>기타 모니터링</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>그리드 레이아웃</InputLabel>
            <Select
              value={gridSize}
              label="그리드 레이아웃"
              onChange={handleGridSizeChange}
            >
              <MenuItem value="auto" sx={{ fontWeight: 400 }}>위치 기반 (행-열)</MenuItem>
              <MenuItem value="1x2" sx={{ fontWeight: 400 }}>1 x 2</MenuItem>
              <MenuItem value="2x2" sx={{ fontWeight: 400 }}>2 x 2</MenuItem>
              <MenuItem value="2x3" sx={{ fontWeight: 400 }}>2 x 3</MenuItem>
              <MenuItem value="3x3" sx={{ fontWeight: 400 }}>3 x 3</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<ViewModuleIcon />}
            size="small"
            onClick={toggleFitMode}
            sx={{ minWidth: 'auto' }}
          >
            {fitMode === 'contain' ? '여백 없이 보기' : '원본 비율로 보기'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            size="small"
            onClick={openGridInNewWindow}
            disabled={getFilteredCCTVs().length === 0}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            새창에서 전체보기
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchCCTVs}
            disabled={loading}
          >
            {loading ? '로딩 중...' : '새로고침'}
          </Button>
        </Box>
      </Box>

      {/* 정보 표시 영역 */}
      {error && renderError()}
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flexGrow: 1, 
        overflow: 'auto', 
        bgcolor: 'background.default', 
        borderRadius: 1,
        p: 2
      }}>
        {loading ? renderLoading() : renderCCTVGrid()}
      </Box>

      {/* 하단 상태 표시 */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          표시: {getFilteredCCTVs().length} / 전체: {cctvs.length} 카메라
        </Typography>
        <Typography variant="caption" color="text.secondary">
          마지막 업데이트: {new Date().toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
});

export default RealCCTVTab; 